import styles from './profile-setup-page.module.css';
import { Checkbox } from '@/components/inputs/checkbox';
import { z } from 'zod';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/form';
import { useTranslation } from '@/contexts/i18n-context';
import { useNavigate } from '@/util/react-router-dom';
import { RadioGroup } from '@/components/inputs/radio-group';
import clsx from 'clsx';
import { GradientFormTitle } from '@/components/containers/gradient-form-title';
import { Section } from '@/components/containers/section';
import { ProfileCreationNavigation } from '@/components/navigation/profile-creation-navigation';
import { useProfileCreation } from '@/contexts/profile-creation-context';
import { PageContainer } from '@/components/containers/page-container';
import { FormButtonRow } from '@/components/containers/form-button-row';
import { useUpdatePatientSurrogate } from '@/api/update-surrogate-info';
import { useLoggedUser } from '@/contexts/current-user-context';

const schema = z.object({
  visitFor: z.union([z.literal('myself'), z.literal('surrogate')]),
  ageConfirmation: z.boolean(),
});

type FormValues = z.infer<typeof schema>;

const OPTIONS = ['myself', 'surrogate'] as const;

export function ProfileSetupPage() {
  const { t } = useTranslation();
  const { user } = useLoggedUser();
  const { mutate: saveSurrogateInfo, isPending } = useUpdatePatientSurrogate();

  const {
    aboveEighteen,
    visitFor: visitForContext,
    profileTypeCompleted,
    setAboveEighteen,
    setVisitFor,
    setProfileTypeCompleted,
    patient,
  } = useProfileCreation();

  const values: FormValues =
    profileTypeCompleted || !!patient?.dateOfBirth
      ? {
          visitFor: visitForContext,
          ageConfirmation: aboveEighteen,
        }
      : {
          visitFor: 'myself',
          ageConfirmation: false,
        };

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    values,
  });

  const visitFor = form.watch('visitFor');
  const ageConfirmation = form.watch('ageConfirmation');

  const navigate = useNavigate();
  const over18Required = visitFor === 'surrogate' && !ageConfirmation;

  const handleVisitForChange = (value: string) => {
    setVisitFor(value as 'myself' | 'surrogate');
  };

  const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
    setVisitFor(data.visitFor);
    setAboveEighteen(data.ageConfirmation);
    setProfileTypeCompleted(true);
    if (patient?.surrogate && visitFor === 'myself' && ageConfirmation) {
      saveSurrogateInfo(
        {
          patientId: user.id,
          data: {
            surrogate: null,
            textPatient: false,
          },
        },
        {
          onSuccess: () => navigate('/profile/patient-info'),
        },
      );
    } else {
      navigate('/profile/patient-info');
    }
  };

  return (
    <PageContainer className={styles.pageContainer}>
      <GradientFormTitle>
        <ProfileCreationNavigation />
        <h1 className={clsx('h3', styles.pageTitle)}>{t('patientProfileSetup.pageTitle')}</h1>
        <p>{t('patientProfileSetup.pageDescription')}</p>
      </GradientFormTitle>

      <Form form={form} onSubmit={onSubmit} autoComplete="off">
        <Section>
          <h2 className="h4">{t('patientProfileSetup.section.title')}</h2>
          <p className={styles.sectionDescription}>
            {t('patientProfileSetup.section.description')}
          </p>
          <ul className={styles.conditionsList}>
            <li>
              <span>{t('patientProfileSetup.section.conditions.condition1')}</span>
            </li>
            <li>
              <span>{t('patientProfileSetup.section.conditions.condition2')}</span>
            </li>
          </ul>

          <RadioGroup
            name="visitFor"
            label={t('patientProfileSetup.section.optionsLabel')}
            readersOnlyLabel
            className={styles.visitForSelector}
            options={OPTIONS.map((option) => ({
              value: option,
              label: t(`patientProfileSetup.section.options.${option}`),
            }))}
            onChange={(e) => handleVisitForChange(e.target.value)}
          />
        </Section>
        <div className={styles.ageConfirmationSection}>
          <Checkbox
            className={styles.ageConfirmationCheckbox}
            label={t('patientProfileSetup.section.ageConfirmation')}
            name="ageConfirmation"
            error={
              over18Required ? t('patientProfileSetup.section.ageConfirmationError') : undefined
            }
            onChange={(e) => setAboveEighteen(e.target.checked)}
          />
        </div>
        <FormButtonRow
          nextStep={t('patientProfileSetup.section.nextStep')}
          buttonTextKey="common.next"
          type="submit"
          rightIcon="arrow_forward"
          disabled={over18Required}
          loading={isPending}
        />
      </Form>
    </PageContainer>
  );
}
